import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { integrationApis } from "../../../api/integrationApis";
import { siRatingApis } from "../../../api/siRatingApis";

const REPORTING_PERIODS = ["Annual", "Quarterly", "Monthly"];

function errorMessage(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

export default function SiRatingRunForm({ onCreated }) {
  const [projects, setProjects] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [projectId, setProjectId] = useState("");
  const [reportingPeriod, setReportingPeriod] = useState("Annual");
  const [ratingSector, setRatingSector] = useState("");
  const [year, setYear] = useState("");
  const [needsSector, setNeedsSector] = useState(false);
  const [files, setFiles] = useState([]);
  const [fileRole, setFileRole] = useState("gap");

  useEffect(() => {
    let active = true;
    Promise.all([integrationApis.getSdmProjects().catch((e) => ({ error: e })), siRatingApis.listSectors().catch(() => [])])
      .then(([projectRes, sectorList]) => {
        if (!active) return;
        if (projectRes?.error) {
          setError(
            errorMessage(projectRes.error, "Could not load SDM projects. Sign in via GE3S SSO and make sure SDM is configured.")
          );
        }
        const list = projectRes?.projects || [];
        setProjects(list);
        if (list.length) setProjectId(list[0].id);
        setSectors(sectorList);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const selectedProject = projects.find((p) => String(p.id) === String(projectId));

  const handleRun = async () => {
    if (!projectId) return;
    setRunning(true);
    setError("");
    try {
      const rating = await siRatingApis.run({
        projectId,
        projectName: selectedProject?.name,
        reportingPeriod,
        siteId: "global",
        year: year || selectedProject?.reportingYear || undefined,
        ratingSector: ratingSector || undefined,
      });
      let latest = rating;
      if (files.length) {
        latest = await siRatingApis.uploadPdfReports(rating._id, files, { role: fileRole, year: year || undefined });
      }
      setFiles([]);
      setNeedsSector(false);
      onCreated?.(latest || rating);
    } catch (err) {
      const msg = errorMessage(err, "SI Rating failed");
      if (err?.response?.status === 422) setNeedsSector(true);
      setError(msg);
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="si-rating-card si-rating-card--center">
        <CircularProgress size={28} />
      </div>
    );
  }

  return (
    <div className="si-rating-card">
      <h2>Run SI Rating</h2>
      <p className="si-rating-muted">
        Scores your SDM data against the sector KPI set (AHP weights, peer / best-practice bounds) and produces the
        scoring workbook and the Comprehensive Sustainability Rating Report.
      </p>

      <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
        <InputLabel id="si-rating-project">SDM project</InputLabel>
        <Select
          labelId="si-rating-project"
          label="SDM project"
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          disabled={!projects.length}
        >
          {projects.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.name}
              {p.sector ? ` — ${p.sector}` : ""}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <div className="si-rating-form-row">
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel id="si-rating-period">Reporting period</InputLabel>
          <Select
            labelId="si-rating-period"
            label="Reporting period"
            value={reportingPeriod}
            onChange={(e) => setReportingPeriod(e.target.value)}
          >
            {REPORTING_PERIODS.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Reporting year"
          value={year}
          onChange={(e) => setYear(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
          placeholder={selectedProject?.reportingYear ? String(selectedProject.reportingYear) : "e.g. 2025"}
          sx={{ flex: 1 }}
        />
      </div>

      <FormControl fullWidth size="small" sx={{ mb: 1.5 }} error={needsSector && !ratingSector}>
        <InputLabel id="si-rating-sector">Rating sector</InputLabel>
        <Select
          labelId="si-rating-sector"
          label="Rating sector"
          value={ratingSector}
          onChange={(e) => setRatingSector(e.target.value)}
        >
          <MenuItem value="">Detect from SDM project</MenuItem>
          {sectors.map((s) => (
            <MenuItem key={s.name} value={s.name}>
              {s.name} ({s.kpiCount} KPIs)
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <div className="si-rating-upload">
        <label className="si-rating-upload__label">
          Previous sustainability / annual reports (optional, PDF)
          <input
            type="file"
            accept="application/pdf"
            multiple
            onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 5))}
          />
        </label>
        {files.length > 0 && (
          <div className="si-rating-form-row">
            <span className="si-rating-muted">{files.map((f) => f.name).join(", ")}</span>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="si-rating-file-role">Use values to</InputLabel>
              <Select
                labelId="si-rating-file-role"
                label="Use values to"
                value={fileRole}
                onChange={(e) => setFileRole(e.target.value)}
              >
                <MenuItem value="gap">Fill gaps in this year</MenuItem>
                <MenuItem value="baseline">Previous-year baseline</MenuItem>
              </Select>
            </FormControl>
          </div>
        )}
      </div>

      {error && <p className="si-rating-error">{error}</p>}

      <Button
        variant="contained"
        fullWidth
        onClick={handleRun}
        disabled={running || !projectId}
        startIcon={running ? <CircularProgress size={16} color="inherit" /> : <PlayArrowRoundedIcon />}
        sx={{ mt: 1, bgcolor: "#02b880", "&:hover": { bgcolor: "#029a6c" } }}
      >
        {running ? "Scoring…" : "Run SI Rating"}
      </Button>
    </div>
  );
}
